/**
 * Copyright ToolBelt Data Inc., 2020 All Rights Reserved
 */

import React, {useState} from "react";
import { Col, Container, Form, Row } from "react-bootstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { first, startCase } from "lodash";

import styled from "styled-components";

import ChatBubble from "../../common/components/ChatBubble";
import ChatFeed from "../../common/components/ChatFeed";
import { Doughnut } from "react-chartjs-2";
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend
} from 'chart.js';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';

import ChatInput from "./components/ChatInput";

import { isArrayOfJSON, isValidJSON, replaceNullValues } from "../../common/Utils";

import { APP_COLORS } from "../../common/Constants";
import { SAMPLE_QUESTIONS } from "./SampleQuestions";

import FormContainer from "../../common/components/layout/FormContainer";

import { faGrid2 } from "@fortawesome/pro-solid-svg-icons";
import { faCopy } from "@fortawesome/free-regular-svg-icons";

const ChatRoomContainer = (props) => {
    const {
        currentConversationId,
        currentConversationName,
        isLoading,
        messages,
        isTyping,
        onSubmit,
        onSampleQuestionSelected
    } = props;

    const [isCopied, setIsCopied] = useState(false);
    const [showSampleQuestions, setShowSampleQuestions] = useState(true);

    const toggleSampleQuestions = () => {
        // setShowSampleQuestions(!showSampleQuestions);
        console.log(showSampleQuestions);
    };

    const SampleQuestionItem = styled.div `
        font-size: 0.9rem;
        padding: 5px 10px 10px 5px;
        border-radius: 4px;
        color: ${APP_COLORS['orange']};
        background-color: transparent;
        cursor: pointer;
        transition: background-color 0.2s ease;
        justify-self: flex-start;
        font-size-adjust: from-font;
    `;

    const customBubble = (props) => {
        // Determine if this is a user message (id: 0) or bot message (id: 1)
        const isUser = props.message.id === 0;
        
        if (props.message && props.message.message && isValidJSON(props.message.message) && isArrayOfJSON(props.message.message)) {
            const jsonData =  JSON.parse(props.message.message);
            const returnResult = replaceNullValues(jsonData);
            const headers = Object.keys(first(returnResult));
            const allKeys = [...new Set(returnResult.flatMap(Object.keys))];

            // Identify meaningful keys (assumes highest numeric value as dataset)
            const labelKey = allKeys.find(key => typeof returnResult[0]?.[key] === 'string') || 'Unknown';
            const dataKey = allKeys.find(key => typeof returnResult[0]?.[key] === 'number') || 'Unknown';

            // Extract data for chart
            const labels = returnResult.map(entry => entry[labelKey] || 'Unknown');
            const dataValues = returnResult.map(entry => entry[dataKey] || 0);

            const restIsCopied = async () => {
                setIsCopied(false);
            }

            const copyResult = async () => {
                navigator.clipboard.writeText(JSON.stringify(returnResult))
                    .then(() => {
                        setIsCopied(true);
                    })
                    .catch(err => console.error("Failed to copy: ", err));

                setTimeout(restIsCopied, 3000);
            };

            const chartData = {
                labels: labels,
                datasets: [
                    {
                        label: dataKey.replace(/_/g, ' '),  // Display data key dynamically
                        data: dataValues,
                        backgroundColor: [
                            'rgba(54, 162, 235, 0.6)',
                            'rgba(255, 99, 132, 0.6)',
                            'rgba(255, 206, 86, 0.6)',
                            'rgba(75, 192, 192, 0.6)',
                            'rgba(153, 102, 255, 0.6)',
                            'rgba(255, 159, 64, 0.6)',
                            'rgba(55, 162, 235, 0.6)',
                            'rgba(25, 99, 132, 0.6)',
                            'rgba(44, 206, 86, 0.6)',
                            'rgba(56, 192, 192, 0.6)',
                            'rgba(77, 102, 255, 0.6)',
                            'rgba(88, 159, 64, 0.6)',
                            'rgba(99, 162, 235, 0.6)',
                            'rgba(100, 99, 132, 0.6)',
                            'rgba(101, 206, 86, 0.6)',
                            'rgba(102, 192, 192, 0.6)',
                            'rgba(103, 102, 255, 0.6)',
                            'rgba(104, 159, 64, 0.6)',
                            'rgba(105, 102, 255, 0.6)',
                            'rgba(106, 159, 64, 0.6)',
                        ],
                        borderColor: 'rgba(54, 162, 235, 1)',
                        borderWidth: 1,
                    }
                ]
            };

            // Chart Options
            const chartOptions = {
                responsive: true,
                maintainAspectRatio: false,  // ✅ Important for size control
                legend: {
                    display: true  // 👈 Hides the legend
                },
                scales: {
                    y: {
                        beginAtZero: true
                    }
                }
            };

            // Store the chart/table content in a variable
            const chartTableContent = (
                <div>
                    <p>
                        <div style={{overflowX: 'auto', marginBottom: '10px'}}>
                            <table className="table table-striped table-bordered">
                                <thead>
                                    <tr>
                                        {headers.map((header, index) => (
                                            <th key={index} style={{fontSize: '12px', padding: '8px'}}>
                                                {header.replace(/_/g, ' ')}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {returnResult.map((entry, index) => (
                                        <tr key={index}>
                                            {headers.map((key, keyIndex) => (
                                                <td key={keyIndex} style={{fontSize: '12px', padding: '8px'}}>
                                                    {entry[key] !== null && entry[key] !== undefined
                                                        ? (typeof entry[key] === 'number'
                                                            ? entry[key].toLocaleString()
                                                            : entry[key])
                                                        : 'N/A'}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {(returnResult.length >1 && returnResult.length <= 20) && (
                            <div style={{width: '80%', height: '400px', margin: '0 auto'}}>
                                <Doughnut data={chartData} options={chartOptions}/>
                            </div>
                        )}
                    </p>
                    <p>
                        <div className="relative group">
                            <button
                                onClick={copyResult}
                                className="text-blue-500 text-sm hover:text-blue-600 transition-all appearance-none outline-none border-none bg-transparent p-1"
                                style={{border: 'none', boxShadow: 'none'}}
                            >
                                <FontAwesomeIcon icon={faCopy} className="text-sm"/>
                            </button>
                            <span className={`absolute -top-8 left-1/2 -translate-x-1/2 text-xs px-2 py-1 rounded bg-black text-white opacity-0 group-hover:opacity-100 transition ${true ? 'opacity-100 bg-green-600' : ''}`}>
                                {isCopied ? 'Copied!' : ''}
                            </span>
                        </div>
                    </p>
                </div>
            );
            
            // Wrap the chart/table content with proper bubble styling
            return (
                <div style={{
                    backgroundColor: '#ffffff',
                    marginBottom: '10px',
                    display: 'flex',
                    justifyContent: isUser ? 'flex-end' : 'flex-start'
                }}>
                    <div style={{
                        borderRadius: '20px',
                        margin: '1px auto',
                        maxWidth: '170%',
                        padding: '10px',
                        width: 'fit-content',
                        float: isUser ? 'right' : 'left',
                        borderStyle: 'solid',
                        borderWidth: 1,
                        borderColor: APP_COLORS['gray-400'],
                        marginLeft: isUser ? 'auto' : '20px',
                        marginRight: isUser ? '20px' : 'auto',
                        backgroundColor: isUser ? '#f8f9fa' : '#ffffff',
                    }}>
                        {chartTableContent}
                    </div>
                </div>
            );
        } else {
            return <ChatBubble
                bubbleStyles={{
                    text: {
                        fontSize: 15,
                        color: 'black',
                    },
                    chatbubble: {
                        backgroundColor: APP_COLORS['white'],
                    },
                    userBubble: {
                        borderRadius: 15,
                        padding: '10px 15px',
                        maxWidth: '70%',
                        backgroundColor: APP_COLORS['gray-200'],
                    }
                }}
                message={props.message}
            />
        }
    }

    if (!currentConversationId) {
        return (
            <>
                <div className="text-center" style={{marginTop: '130px'}}>
                    <p className="display-5 home font-weight-bold mb-5" style={{color: APP_COLORS['tech-blue']}}>
                        What can I help with?

                        <FontAwesomeIcon
                            icon={faInfoCircle}
                            style={{fontSize: '18px', cursor: 'pointer', marginLeft: '10px'}}
                            onClick={toggleSampleQuestions}
                            title="Show sample questions"
                        />
                    </p>
                    <div style={{maxWidth: '600px', margin: '0 auto'}}>
                        <ChatInput onSubmit={onSubmit} isLoading={isLoading} />
                    </div>
                </div>

                <div className="text-center" style={{marginTop: '10px'}}>
                    {showSampleQuestions && <div>
                        <Container>
                            <FormContainer>
                                <Form noValidate>
                                    <Row style={{
                                        marginTop: '20px',
                                        paddingBottom: '10px',
                                        fontSize: '18px',
                                    }}>
                                        <Col md={12}>
                                            <small className="text-muted font-italic fw-light text-sm-left d-block">
                                                Ask about Companies or Contacts in the Residential Construction Industry. You can ask to find companies based on industry specific characteristics like the type of work they do or where they do that work. You can also ask about the different types of activity they are engaged in. For instance, the number of jobs they work or how much they spend in different product categories.
                                            </small>
                                        </Col>
                                    </Row>
                                    <Row style={{
                                        marginTop: '10px',
                                        paddingBottom: '0px'
                                    }}>
                                        <Col md={12}> <div className="text-muted font-weight-bolder text-md-left" > For Example: </div></Col>
                                    </Row>
                                    <Row style={{
                                        marginTop: '10px',
                                        paddingBottom: '0px',
                                    }}>
                                        <Col md={6}>
                                            <SampleQuestionItem
                                                key={SAMPLE_QUESTIONS.SHOW_ME_BY_TRADE_ALL_CONTRACTOR_IN_COLORADO.id}
                                                onClick={() => onSampleQuestionSelected(SAMPLE_QUESTIONS.SHOW_ME_BY_TRADE_ALL_CONTRACTOR_IN_COLORADO.description)}
                                                style={{
                                                    backgroundColor: 'transparent'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.backgroundColor = APP_COLORS['gray-200'];
                                                }}
                                                onMouseLeave={(e) => {
                                                        e.currentTarget.style.backgroundColor = 'transparent';
                                                }}>
                                            <FontAwesomeIcon icon={faGrid2} size="lg" color={APP_COLORS['orange']} style={{ paddingLeft: '10px', paddingTop: '10px', paddingRight: '10px' }} onClick={toggleSampleQuestions} />
                                                <span  dangerouslySetInnerHTML={{ __html: SAMPLE_QUESTIONS.SHOW_ME_BY_TRADE_ALL_CONTRACTOR_IN_COLORADO.display }} />
                                            </SampleQuestionItem>
                                        </Col>
                                        <Col md={6}>
                                            <SampleQuestionItem
                                                key={SAMPLE_QUESTIONS.SHOW_ME_THE_TOTAL_SPEND_BY_PRODUCT_CATEGORY_IN_TEXAS.id}
                                                onClick={() => onSampleQuestionSelected(SAMPLE_QUESTIONS.SHOW_ME_THE_TOTAL_SPEND_BY_PRODUCT_CATEGORY_IN_TEXAS.description)}
                                                style={{
                                                    backgroundColor: 'transparent'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.backgroundColor = APP_COLORS['gray-200'];
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = 'transparent';
                                                }}>
                                                <FontAwesomeIcon icon={faGrid2} size="lg" color={APP_COLORS['orange']} style={{ paddingLeft: '10px', paddingTop: '10px', paddingRight: '10px' }} onClick={toggleSampleQuestions} />
                                                <span dangerouslySetInnerHTML={{ __html: SAMPLE_QUESTIONS.SHOW_ME_THE_TOTAL_SPEND_BY_PRODUCT_CATEGORY_IN_TEXAS.display }} />
                                            </SampleQuestionItem>
                                        </Col>
                                    </Row>
                                    <Row style={{
                                        marginTop: '10px',
                                        paddingBottom: '0px'
                                    }}>
                                        <Col md={6}>
                                            <SampleQuestionItem
                                                key={SAMPLE_QUESTIONS.TOP_5_BUILDERS_IN_TEXAS_BASED_ON_REVIEWS.id}
                                                onClick={() => onSampleQuestionSelected(SAMPLE_QUESTIONS.TOP_5_BUILDERS_IN_TEXAS_BASED_ON_REVIEWS.description)}
                                                style={{
                                                    backgroundColor: 'transparent'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.backgroundColor = APP_COLORS['gray-200'];
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = 'transparent';
                                                }}>
                                                <FontAwesomeIcon icon={faGrid2} size="lg" color={APP_COLORS['orange']} style={{ paddingLeft: '10px', paddingTop: '10px', paddingRight: '10px' }} onClick={toggleSampleQuestions} />
                                                <span dangerouslySetInnerHTML={{ __html: SAMPLE_QUESTIONS.TOP_5_BUILDERS_IN_TEXAS_BASED_ON_REVIEWS.display }} />
                                            </SampleQuestionItem>
                                        </Col>
                                        <Col md={6}>
                                            <SampleQuestionItem
                                                key={SAMPLE_QUESTIONS.TOP_10_SPEND_ON_PLUMBING_FIXTURES.id}
                                                onClick={() => onSampleQuestionSelected(SAMPLE_QUESTIONS.TOP_10_SPEND_ON_PLUMBING_FIXTURES.description)}
                                                style={{
                                                    backgroundColor: 'transparent'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.backgroundColor = APP_COLORS['gray-200'];
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = 'transparent';
                                                }}>
                                                <FontAwesomeIcon icon={faGrid2} size="lg" color={APP_COLORS['orange']} style={{ paddingLeft: '10px', paddingTop: '10px', paddingRight: '10px' }} onClick={toggleSampleQuestions} />
                                                <span dangerouslySetInnerHTML={{ __html: SAMPLE_QUESTIONS.TOP_10_SPEND_ON_PLUMBING_FIXTURES.display }} />
                                            </SampleQuestionItem>
                                        </Col>
                                    </Row>
                                    <Row style={{
                                        marginTop: '10px',
                                        paddingBottom: '0px'
                                    }}>
                                        <Col md={6}>
                                            <SampleQuestionItem
                                                key={SAMPLE_QUESTIONS.SHOW_ME_COMPANIES_IN_AUSTIN_THAT_SPEND_OVER_10K_ON_LENNOX_LAST_YEAR.id}
                                                onClick={() => onSampleQuestionSelected(SAMPLE_QUESTIONS.SHOW_ME_COMPANIES_IN_AUSTIN_THAT_SPEND_OVER_10K_ON_LENNOX_LAST_YEAR.description)}
                                                style={{
                                                    backgroundColor: 'transparent'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.backgroundColor = APP_COLORS['gray-200'];
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = 'transparent';
                                                }}>
                                                <FontAwesomeIcon icon={faGrid2} size="lg" color={APP_COLORS['orange']} style={{ paddingLeft: '10px', paddingTop: '10px', paddingRight: '10px' }} onClick={toggleSampleQuestions} />
                                                <span dangerouslySetInnerHTML={{ __html: SAMPLE_QUESTIONS.SHOW_ME_COMPANIES_IN_AUSTIN_THAT_SPEND_OVER_10K_ON_LENNOX_LAST_YEAR.display }} />
                                            </SampleQuestionItem>
                                        </Col>
                                        <Col md={6}>
                                            <SampleQuestionItem
                                                key={SAMPLE_QUESTIONS.SHOW_ME_ELECTRICIANS_WITH_MORE_THAN_$1_MILLION_IN_ANNUAL_SALES_IN_THE_NORTHEAST.id}
                                                onClick={() => onSampleQuestionSelected(SAMPLE_QUESTIONS.SHOW_ME_ELECTRICIANS_WITH_MORE_THAN_$1_MILLION_IN_ANNUAL_SALES_IN_THE_NORTHEAST.description)}
                                                style={{
                                                    backgroundColor: 'transparent'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.backgroundColor = APP_COLORS['gray-200'];
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.backgroundColor = 'transparent';
                                                }}>
                                                <FontAwesomeIcon icon={faGrid2} size="lg" color={APP_COLORS['orange']} style={{ paddingLeft: '10px', paddingTop: '10px', paddingRight: '10px' }} onClick={toggleSampleQuestions} />
                                                <span dangerouslySetInnerHTML={{ __html: SAMPLE_QUESTIONS.SHOW_ME_ELECTRICIANS_WITH_MORE_THAN_$1_MILLION_IN_ANNUAL_SALES_IN_THE_NORTHEAST.display }} />
                                            </SampleQuestionItem>
                                        </Col>
                                    </Row>
                                </Form>
                            </FormContainer>
                        </Container>
                    </div>}
                </div>
            </>
        );
    }

    return (
        <div style={{ transition: 'padding-left 0.3s ease', width: '100%' }}>
            <p className="display-5 mb-4" style={{ color: APP_COLORS['tech-blue'] }}>
                {currentConversationName} {currentConversationId && <span className="display-5 mb-4" style={{ color: APP_COLORS['gray-500'], fontSize: 12 }}>({currentConversationId})</span>}
            </p>

            <div style={{ width: '100%' }}>
                <ChatFeed
                    messages={messages || []}
                    isTyping={isTyping}
                    hasInputField={false}
                    showSenderName={false}
                    bubblesCentered={false}
                    customBubble={customBubble}
                />
            </div>

            <div style={{ width: '100%', justifyItems: 'center' }}>
                <ChatInput onSubmit={onSubmit} isLoading={isLoading} />
            </div>
        </div>
    );
}

export default ChatRoomContainer;

